import { forwardRef, useEffect, useRef, useState } from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { checkboxState, CIFType, cifTypeState, compareListState, geoState, selectedChildState } from '../../states';
import { Body, BodyColor, BodyType, Button, ButtonSize, ButtonTheme, ButtonVariant, Heading, TableMenu, TypographyColor, TypographyType } from '../../ui-elements';
import { PresenceToolTip } from '.';
import { expandedHeaderBreakdownTableState, selectedCardState, selectedChilddashboardState, buttonStateRetainedState, breakdownTableSortState } from '../../states/DashboardState';
import { useNavigate } from 'react-router-dom';
import { RouteConstants } from '../../constants';
import { useCompareService } from '../../services';

interface tableModelProps {
    tableContent: any
    searchTerm: string
    ref?: string
    setSelTableHeader: (selTableHeader: any) => void,
    selTableHeader: any,
    setIsOpenDrawer?: (value: boolean) => void,
    setIsrequestFromTable?: (value: boolean) => void,
    setGeoName?: (value: any) => void,
    type: string
    retainValue?: boolean  // New prop to control retention
}

export const BreakDownTableComponent = forwardRef<HTMLDivElement, tableModelProps>(({ 
    tableContent, 
    searchTerm, 
    setSelTableHeader, 
    selTableHeader, 
    setIsOpenDrawer, 
    setIsrequestFromTable, 
    setGeoName, 
    type,
    retainValue = false  // Default to false
}, ref) => {
   
    const navigate = useNavigate();
    const compareService = useCompareService();
    const [tableData, setTableData] = useState(tableContent);
    const isChecked = useRecoilValue(checkboxState);
    const cifType = useRecoilValue(cifTypeState);
    
    // Safely get keys from tableData.tableData
    const keys = tableData?.tableData?.length > 0 ? Object.keys(tableData.tableData[0]) : [];
    const filteredData = keys.filter((item: any) => item !== 'geoId' &&  item !== "actualArea" && item !== "actualPopulation" && item !== "actaulEh" && item !== "actualPoiCount" && item !== "actualCtv" && item !== 'country' && item !== 'state' && item !== 'district' && item !== 'sub_district' && item !== 'village' && item !== 'myPresence' && item !== 'geoLevelNumber' );
    const tableheader = tableData?.tableHeaderData || [];
    const [suggestions, setSuggestions] = useState<any[]>([]);
    
    const selectedCardType = useRecoilValue(selectedCardState);
    const [buttonStateRetained, setButtonStateRetained] = useRecoilState(buttonStateRetainedState);
    
    // Get the parent component type based on selectedCardType
    const getParentComponentType = (): 'EhData' | 'cTVData' | 'poiData' | null => {
        if (selectedCardType.poiData) return 'poiData';
        if (selectedCardType.cTVData) return 'cTVData';
        if (selectedCardType.EhData) return 'EhData';
        return null;
    };

    const parentComponentType = getParentComponentType();

    // Use Recoil state for sorting so it persists across remounts
    const [allSortStates, setAllSortStates] = useRecoilState(breakdownTableSortState);
    const sortState = parentComponentType ? allSortStates[parentComponentType] : { sorting: false, buttonState: [2, false] as [number, boolean] };
    const sorting = sortState.sorting;
    const buttonState = sortState.buttonState;

    const setSortState = (newState: { sorting: boolean; buttonState: [number, boolean] }) => {
        if (parentComponentType) {
            setAllSortStates(prev => ({
                ...prev,
                [parentComponentType]: newState
            }));
        }
    };

    const [columnName, setColumnName] = useState<CIFType[]>(cifType);
    const [isExpanded, setIsExpanded] = useState(true); // Track whether table is expanded
    const setSelectedGeo = useSetRecoilState(geoState);
    
    const [hoveredPresence, setHoveredPresence] = useState<{ row: number; index: number } | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [openedMenu, setOpenedMenu] = useState<number | null>(null);
    const compareList = useRecoilValue(compareListState);

    const currCompareList = (tableheader?.length > 0 && tableheader?.[1]?.label === 'District') ? new Set(compareList?.districts?.map((i: any) => i?.geoId)) : tableheader?.[1]?.label === 'Pincode' ? new Set(compareList?.pincodes?.map((i: any) => i?.geoId)) : tableheader?.[1]?.label === 'Village' ? new Set(compareList?.villages?.map((i: any) => i?.geoId)) : new Set(compareList?.subdistricts?.map((i: any) => i?.geoId))
    
    const COLS = [
        { id: 'view_map', label: 'View on Map', icon: 'edit', onClick: (item: any) => navigateTo(item, RouteConstants.root) },
        { id: 'add_compare', label: 'Add to compare', icon: 'add', onClick: (item: any) => handleCompareAction(item), isDisabled: currCompareList.size >= 9 },
        { id: 'view_comparison', label: 'View Comparisons', icon: 'north_west', onClick: (item: any) => handleViewComparison(item) },
    ];

    const [expandedHeaderBreakdownTable, setExpandedHeaderBreakdownTable] = useRecoilState(expandedHeaderBreakdownTableState);
    const [selectedChild, setSelectedChild] = useRecoilState(selectedChilddashboardState); // EH column default
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Save buttonState to Recoil when it changes (only if retainValue is true)
    useEffect(() => {
        if (retainValue && parentComponentType) {
            setButtonStateRetained(prev => ({
                ...prev,
                [parentComponentType]: buttonState
            }));
        }
    }, [buttonState]);

    useEffect(() => {
        if (!searchTerm) {
            setSuggestions([]); // Clear suggestions when search term is empty
        } else if (tableData?.tableData && tableheader?.length > 0) {
            const lowercasedValue = searchTerm.toLowerCase();
            const result = tableData.tableData.filter((item: any) => {
                // Loop over keys of the item object and check for a match
                return Object.keys(item).some((key: string) => {
                    const lowercasedItemValue = item[key]?.toString().toLowerCase();
                    return lowercasedItemValue?.includes(lowercasedValue);
                });
            });
            setSuggestions(result || []);
        }
    }, [searchTerm, tableData]);

    const getSelectedMetricPayload = () => {
        if (!tableContent) return;
        const headerData = tableContent?.tableHeaderData;

        const result: {
            eh: string;
            ctv: string;
            PoI: string;
        } = { eh: "", ctv: "", PoI: "" };

        headerData?.forEach((header: any) => {
            let key: keyof typeof selectedChild | null = null;

            if (header?.label?.includes("No.")) key = "EH";
            if (header?.label?.startsWith("CTV")) key = "CTV";
            if (header?.label?.startsWith("PoI")) key = "PoI";

            if (key && header?.children && selectedChild?.[key] !== undefined) {
                const selectedLabel = header?.children?.[selectedChild?.[key]]?.label;
                if (selectedLabel) {
                    result[key.toLowerCase() as "eh" | "ctv" | "PoI"] = selectedLabel;
                }
            }
        });

        return result;
    };

    useEffect(() => {
        const data = getSelectedMetricPayload();
        // Only update if changed
        if (JSON.stringify(data) !== JSON.stringify(selTableHeader)) {
            setSelTableHeader(data);
        }
    }, [selectedChild]);

    const columnNames = filteredData; // Define the column names in the correct order

    const handleSortTable = (order: string, index: number) => {
        const newButtonState: [number, boolean] = [index, order === 'asc'];
        setSortState({ sorting: true, buttonState: newButtonState });

        if (tableData?.tableData && tableData.tableData.length > 0) {
            // Map column name
            const columnName = (() => {
                const name = columnNames[index];
                switch (name) {
                    case 'area': return "actualArea";
                    case 'population': return "actualPopulation";
                    case 'eh': return "actaulEh";
                    case 'poiCount': return "actualPoiCount";
                    case 'ctv': return "actualCtv";
                    default: return name;
                }
            })();

            // force ascending for rank
            const effectiveOrder = columnName === "rankData" ? order : order;
            const finalButtonState: [number, boolean] = [index, effectiveOrder === 'asc'];
            setSortState({ sorting: true, buttonState: finalButtonState });

            const sortedTable = [...tableData.tableData].sort((a, b) => {
                const aValue = columnName == "rankData" ? a?.[columnName]?.rank : a?.[columnName];
                const bValue = columnName == "rankData" ? b?.[columnName]?.rank : b?.[columnName];

                const parseValue = (value: string | number | null | undefined): number | string | null => {
                    if (value === null || value === undefined || value === "" || value === "N/A") return null;
                    if (typeof value === 'number') return value;
                    if (typeof value === 'string') {
                        const parsed = parseFloat(value.replace(/,/g, '').trim());
                        return !isNaN(parsed) ? parsed : value.trim();
                    }
                    return null;
                };

                const parsedA = parseValue(aValue);
                const parsedB = parseValue(bValue);

                // Ensure "N/A" and empty values always go to the end
                if (parsedA === null && parsedB === null) return 0;
                if (parsedA === null) return 1;
                if (parsedB === null) return -1;

                if (typeof parsedA === 'number' && typeof parsedB === 'number') {
                    return order === 'asc' ? parsedA - parsedB : parsedB - parsedA;
                } else if (typeof parsedA === 'string' && typeof parsedB === 'string') {
                    return order === 'asc'
                        ? parsedA.localeCompare(parsedB)
                        : parsedB.localeCompare(parsedA);
                } else if (typeof parsedA === 'number') {
                    return order === 'asc' ? -1 : 1;
                } else {
                    return order === 'asc' ? 1 : -1;
                }
            });
            setTableData({
                ...tableData,
                tableData: [...sortedTable], // Ensure a new reference
            });
        }
    };

    useEffect(() => {
        if (tableContent?.tableData?.length > 0) {
            // Re-apply previous sorting if it exists
            if (sorting && buttonState[0] !== undefined && buttonState[0] !== -1) {
                const index = buttonState[0];
                const isAscending = buttonState[1];

                // Recalculate columnNames from fresh data
                const keys = tableContent.tableData?.length > 0 ? Object.keys(tableContent.tableData[0]) : [];
                const freshColumnNames = keys.filter((item: any) => item !== 'geoId' &&  item !== "actualArea" && item !== "actualPopulation" && item !== "actaulEh" && item !== "actualPoiCount" && item !== "actualCtv" && item !== 'country' && item !== 'state' && item !== 'district' && item !== 'sub_district' && item !== 'village' && item !== 'myPresence' && item !== 'geoLevelNumber' );

                const columnName = (() => {
                    const name = freshColumnNames[index];
                    switch (name) {
                        case 'area': return "actualArea";
                        case 'population': return "actualPopulation";
                        case 'eh': return "actaulEh";
                        case 'poiCount': return "actualPoiCount";
                        case 'ctv': return "actualCtv";
                        default: return name;
                    }
                })();

                const effectiveOrder = isAscending ? 'asc' : 'desc';

                const sortedTable = [...tableContent.tableData].sort((a, b) => {
                    const aValue = columnName == "rankData" ? a?.[columnName]?.rank : a?.[columnName];
                    const bValue = columnName == "rankData" ? b?.[columnName]?.rank : b?.[columnName];

                    const parseValue = (value: string | number | null | undefined): number | string | null => {
                        if (value === null || value === undefined || value === "" || value === "N/A") return null;
                        if (typeof value === 'number') return value;
                        if (typeof value === 'string') {
                            const parsed = parseFloat(value.replace(/,/g, '').trim());
                            return !isNaN(parsed) ? parsed : value.trim();
                        }
                        return null;
                    };

                    const parsedA = parseValue(aValue);
                    const parsedB = parseValue(bValue);

                    if (parsedA === null && parsedB === null) return 0;
                    if (parsedA === null) return 1;
                    if (parsedB === null) return -1;

                    if (typeof parsedA === 'number' && typeof parsedB === 'number') {
                        return effectiveOrder === 'asc' ? parsedA - parsedB : parsedB - parsedA;
                    } else if (typeof parsedA === 'string' && typeof parsedB === 'string') {
                        return effectiveOrder === 'asc'
                            ? parsedA.localeCompare(parsedB)
                            : parsedB.localeCompare(parsedA);
                    } else if (typeof parsedA === 'number') {
                        return effectiveOrder === 'asc' ? -1 : 1;
                    } else {
                        return effectiveOrder === 'asc' ? 1 : -1;
                    }
                });

                setTableData({
                    ...tableContent,
                    tableData: [...sortedTable],
                });
            } else {
                // No sorting active, just set the data as-is
                setTableData(tableContent);
            }
        }
    }, [tableContent]);

    const handleLoadMore = () => {
        setIsExpanded(false);
    };

    const handleGeoNameClick = (item: any) => {
        const { country, state, district, sub_district, village } = item || {};
        setSelectedGeo({ country, state, district, sub_district, village });
    }

    const navigateTo = (item: any, routeTo: string) => {
        const params = new URLSearchParams();
        const selGeo: Record<string, any> = {};

        ['country', 'state', 'district', 'sub_district','village'].forEach(key => {
            if (item?.[key] !== undefined && item?.[key] !== null && item?.[key] !== "") {
                params.append(key, item?.[key]);
                selGeo[key] = item?.[key];
            }
        });

        setSelectedGeo(selGeo);

        setTimeout(() =>
            navigate({
                pathname: routeTo,
                search: `?${params.toString()}`,
            }), 0);
    };

    const handleCompareAction = async (item: any) => {
        if (currCompareList?.size >= 9) return;

        const geoId = item?.village || item?.sub_district || item?.district;
        const level = item?.geoLevelNumber;

        const payload = {
            addToCompare: [{ geoId, level, ...(item?.geoLevelNumber === 5 && { parent_id: (item?.district) }) }]
        }
        await compareService?.postCompreList(payload);
    }

    const handleViewComparison = (item: any) => {
        navigate(RouteConstants.compare)
    }

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setExpandedHeaderBreakdownTable(null);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        setExpandedHeaderBreakdownTable(null);
    }, [])

    return (
        <div
            className="breakdown table-container"
            style={{
                maxHeight: '100%',
                overflow: isExpanded ? "hidden" : "auto",
                border: "1px solid #E4E4E4",
                borderRadius: "12px",
                position: "relative", // Make the container a positioning context
            }}
            ref={ref}
        >
            <table
                className="breakdown-table table table-striped w-100"
            >
                <thead>
                    <tr>
                        {tableheader?.map((header: any, index: number) => (
                            <th
                                style={{
                                    backgroundColor:
                                        (header.label === "No. of PoIs" || header.label === "PoI" || header.label === "PoI/Area (sq. km.)") && selectedCardType.poiData
                                            ? "#E5EBFE"
                                            : (header.label === "CTV (₹)" || header.label === "CTV ($)") &&
                                                selectedCardType.cTVData
                                                ? "#DEF9DE" : header.label === "No. of EH" && selectedCardType.EhData ?
                                                    "#FFE0F5" : "#F8F8F8",
                                    position: "sticky",
                                    top: 0, // Keep header sticky
                                    zIndex: 1,
                                    borderTopLeftRadius: index === 0 ? "12px" : "0px",
                                    borderTopRightRadius: index === tableheader.length ? "12px" : "0px",
                                    width:
                                        header.label === "State" ||
                                            header.label === "District" ||
                                            header.label === "Sub District" ||
                                            header.label === "Pincode" ||
                                            header.label === "Village"
                                            ? "250px"
                                            : (header.label?.includes("PoI") ||
                                                header.label?.includes("EH")) && header.label != 'PoI Coverage'
                                                ? "140px" : header.label?.includes("CTV") ? "103px"
                                                : "100px"
                                }}
                                key={header}
                            >
                                <div className="d-flex flex-row align-items-center">
                                    <Body
                                        type={BodyType.p2}
                                        color={BodyColor.secondary}
                                        classname="text-start"
                                    >
{/* {header.children && selectedChild[index] !== undefined
                                            ? header.children[selectedChild[index]].label
                                            : header.label} */}{type === 'poigraph'? header : (header?.children ? (
                                            (() => {
                                                let key: keyof typeof selectedChild | null = null;
                                                if (header?.label?.includes("No.")) key = "EH";
                                                if (header?.label?.startsWith("CTV")) key = "CTV";
                                                if (header?.label?.startsWith("PoI")) key = "PoI";

                                                return key && selectedChild?.[key] !== undefined
                                                    ? header?.children?.[selectedChild?.[key]]?.label
                                                    : header?.label;
                                            })()
                                        ) : (
                                            header?.label
                                        ))}
                                    </Body>
                                    <div className='d-flex gap-2'>
                                        {header?.children && (
                                            <span
                                                style={{
                                                    cursor: "pointer",
                                                    marginLeft: 4,
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                                onClick={e => {
                                                    e?.stopPropagation();
                                                    setExpandedHeaderBreakdownTable(expandedHeaderBreakdownTable === index ? null : index);
                                                }}
                                            >
                                                {expandedHeaderBreakdownTable === index ? <FaAngleUp className="fs-12" /> : <FaAngleDown className="fs-12" />}
                                            </span>
                                        )}
                                        <div className="d-flex flex-column">
                                            <FaAngleUp
                                                name="desc"
                                                className="fs-9"
                                                color={
                                                    buttonState[0] === index && buttonState[1]
                                                        ? "#2B0D5C"
                                                        : "#939393"
                                                }
                                                onClick={() => handleSortTable("asc", index)}
                                            />
                                            <FaAngleDown
                                                name="asc"
                                                className="fs-9"
                                                color={
                                                    buttonState[0] === index && !buttonState[1]
                                                        ? "#2B0D5C"
                                                        : "#939393"
                                                }
                                                onClick={() => handleSortTable("desc", index)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Dropdown for header children */}
                                {header?.children && expandedHeaderBreakdownTable === index && (
                                    <div
                                        ref={dropdownRef}
                                        style={{
                                            position: "absolute",
                                            top: "100%",
                                            left: 0,
                                            background: "#fff",
                                            border: "1px solid #e0e0e0",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                                            borderRadius: 8,
                                            zIndex: 100,
                                            minWidth: 150,
                                            padding: 12,
                                        }}
                                    >
                                        {header?.children?.map((child: any, cidx: number) => {
                                            let key: keyof typeof selectedChild | null = null;
                                            if (header?.label?.includes("No.")) key = "EH";
                                            if (header?.label?.startsWith("CTV")) key = "CTV";
                                            if (header?.label?.startsWith("PoI")) key = "PoI";

                                            if (!key) return null;

                                            return (
                                                <label
                                                    key={child?.label}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 8,
                                                        padding: "4px 0",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`header-${key}`}
                                                        checked={selectedChild?.[key] === cidx}
                                                        onChange={() =>
                                                            setSelectedChild((prev) => ({
                                                                ...prev,
                                                                [key!]: cidx,
                                                            }))
                                                        }
                                                    />
                                                    <Body
                                                        type={BodyType.p3}
                                                        color={BodyColor.secondary}
                                                        classname="p-0"
                                                    >
                                                        {child?.label}
                                                    </Body>
                                                </label>)
                                        })}

                                    </div>
                                )}
                            </th>
                        ))}
                        {tableheader?.length > 0 && (tableheader?.[1]?.label === 'District' || tableheader?.[1]?.label === 'Sub District' || tableheader?.[1]?.label === 'Pincode' || tableheader?.[1]?.label === 'Village') &&
                            <th style={{
                                borderTopRightRadius: '12px', backgroundColor: '#F8F8F8', position: "sticky",
                                top: 0, // Keep header sticky
                                zIndex: 1,
                                width:'30px'
                            }}></th>
                        }
                    </tr>
                </thead>

                <tbody style={{ maxHeight: "45vh", overflowY: "auto" }}>
                    {searchTerm && suggestions?.length > 0 ? (
                        suggestions?.map((item: any, rowIndex: number) => (
                            <tr
                                key={rowIndex}
                                style={{
                                    borderBottomLeftRadius:
                                        rowIndex === suggestions.length - 1 ? "12px" : "0px",
                                    borderBottomRightRadius:
                                        rowIndex === suggestions.length - 1 ? "12px" : "0px",
                                }}
                            >
                                {filteredData?.filter((key) => key !== "presence_types")?.map((key, index) => (
                                    <td
                                        key={key}
                                        className={`
                                                padding-top-5
                                                ${(key === "poiCount" && selectedCardType?.poiData) ? "highlight-column poiColumn"
                                                : (key === "ctv" && selectedCardType?.cTVData) ? "ctvColumn" : key === 'eh' && selectedCardType?.EhData ? "ehColumn" : ""}
                                            `.trim()}
                                        style={{
                                            backgroundColor:
                                                key === "poiCount" && selectedCardType?.poiData
                                                    ? "#E5EBFE"
                                                    : key === "ctv" && selectedCardType?.cTVData
                                                        ? "#DEF9DE "
                                                        : key === 'eh' && selectedCardType?.EhData
                                                            ? "#FFE0F5"
                                                            : "#FFFFFF",
                                        }}
                                    >
                                        {key === "geoName" &&
                                            isChecked?.MBP &&
                                            item?.myPresence !== 0 && item?.myPresence !== "N/A" ? (
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: 'center', height: '4.131vh', gap: '6px' }}>
                                                <span
                                                    className={` ${key === 'geoName' && 'text-underline secondary-hover curser-pointer'}`}
                                                    onClick={key ===  "geoName" ? () => navigateTo(item, RouteConstants.dashboard) : undefined}
                                                >
                                                    {item?.[key]}
                                                </span>
                                                {/* business Presence icon */}
                                                <div
                                                    style={{ paddingBottom: '1.3px', position: 'relative', display: 'flex' }}
                                                    className='gap-2'
                                                >
                                                    {isChecked?.MBP && item?.myPresence !== 0 && item?.myPresence !== "N/A" && item?.presence_types ? (
                                                        item.presence_types?.map((presenceData: any, index: number) => (
                                                            <div className='d-flex flex-row align-items-center justify-content-center' style={{ position: 'relative', display: 'flex', borderRadius: '4px', backgroundColor: 'rgb(173 175 177 / 15%)', height: '23px', gap: "4px", width: "43px" }}
                                                                onMouseEnter={() => setHoveredPresence({ row: rowIndex, index })}
                                                                onMouseLeave={() => setHoveredPresence(null)}
                                                            >
                                                                <img
                                                                    key={index}
                                                                    src={presenceData?.icon}
                                                                    alt=""
                                                                    className=""
                                                                    style={{ height: '1.0vh', }}
                                                                />
                                                                <Heading
                                                                    title={presenceData?.properties?.count}
                                                                    type={TypographyType.h6}
                                                                    colour={TypographyColor.secondary}
                                                                    classname=''
                                                                />
                                                                {hoveredPresence?.row === rowIndex && hoveredPresence?.index === index && (
                                                                    <div style={{ position: 'absolute', bottom: '140%', left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
                                                                        <div
                                                                            style={{
                                                                                position: 'relative',
                                                                                backgroundColor: 'white',
                                                                                padding: '10px 14px',
                                                                                borderRadius: '8px',
                                                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                                                fontSize: '15px',
                                                                                textAlign: 'center',
                                                                                whiteSpace: 'nowrap',
                                                                            }}
                                                                        >
                                                                            <PresenceToolTip presenceData={presenceData} />
                                                                            <div
                                                                                style={{
                                                                                    content: '""',
                                                                                    position: 'absolute',
                                                                                    top: '100%',
                                                                                    left: '50%',
                                                                                    transform: 'translateX(-50%)',
                                                                                    width: 0,
                                                                                    height: 0,
                                                                                    borderLeft: '6px solid transparent',
                                                                                    borderRight: '6px solid transparent',
                                                                                    borderTop: '6px solid white',
                                                                                    borderBottomLeftRadius: '5px',
                                                                                    borderBottomRightRadius: '5px'
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                )}

                                                            </div>
                                                        ))
                                                    ) : null}
                                                </div>
                                            </div>
                                        ) :
                                            key === "rankData" ? <div className='d-flex flex-row gap-1' style={{ alignItems: 'center', height: '4.13vh' }}>
                                                <Heading
                                                    title={key === 'rankData' ? item[key].rank : item[key]}
                                                    type={key === 'rankData' ? TypographyType.h6 : TypographyType.h5}
                                                    colour={TypographyColor.primary}
                                                    classname={`${key === "rankData" && "rankRecommended"}`}
                                                    style={{ backgroundColor: key === 'rankData' ? item.rankData.color : "none" }}
                                                />
                                                <Body
                                                    type={BodyType.p2}
                                                    color={BodyColor.primary}
                                                    classname={'p-0'}
                                                >
                                                    {key === 'rankData' && (item?.[key]?.normalizedScore === null ? "NA" : item?.[key]?.normalizedScore)}
                                                </Body>
                                            </div> : (key === 'poiCount' || selTableHeader[key]?.toLowerCase().includes('poi')) && item[key] == null ?
                                                <div className="d-flex flex-row" style={{ alignItems: 'center', height: '4.131vh' }}>
                                                    <Heading
                                                        title={"Request PoI"}
                                                        type={TypographyType.h5}
                                                        colour={TypographyColor.primary}
                                                        classname={`p-0 text-underline`}
                                                        onClick={() => {
                                                            setIsOpenDrawer?.(true)
                                                            setIsrequestFromTable?.(true);
                                                            setGeoName?.(item?.geoName);
                                                        }}
                                                    />
                                                </div>
                                                :
                                                (
                                                    <div style={{ display: "flex", flexDirection: "row", alignItems: 'center', height: '4.131vh', gap: '3px' }}>
                                                        <span
                                                            className={`${key === 'geoName' && 'text-underline secondary-hover curser-pointer'}`}
                                                            onClick={key === "geoName" ? () => handleGeoNameClick(item) : undefined}
                                                        >
                                                            {item?.[key]}
                                                        </span>
                                                    </div>
                                                )}
                                    </td>
                                ))}
                                 {suggestions?.length > 0 && (tableheader?.[1]?.label === 'District' || tableheader?.[1]?.label === 'Sub District' || tableheader?.[1]?.label === 'Pincode' || tableheader?.[1]?.label === 'Village') &&
                                    <td className="position-relative  d-flex  align-items-center" style={{ height: '7.5vh' }} >
                                        <TableMenu
                                            row={item}
                                            cols={
                                                currCompareList?.has(item?.village || item?.sub_district || item?.district || item?.pincode)
                                                    ? COLS.filter(col => col.id !== 'add_compare')
                                                    : COLS.filter(col => col.id !== 'view_comparison')
                                            }
                                            menuRef={menuRef}
                                            onMenuClick={(flag: boolean | undefined) => setOpenedMenu(flag ? null : openedMenu === rowIndex ? null : rowIndex)}
                                            index={rowIndex}
                                            selRow={openedMenu}
                                            showTooltip={new Map([['add_compare', (currCompareList.size >= 9 && openedMenu === rowIndex)]])}
                                            tooltipText='You have reached the limit of 9 regions. Remove some to add more.'
                                            position={isExpanded && rowIndex >= 4 ? 'top' : 'bottom'}
                                        />
                                    </td>}
                            </tr>
                        ))
                    ) : searchTerm && suggestions.length === 0 ? (
                        <tr>
                            <td colSpan={tableheader.length} style={{ textAlign: "center" }}>
                                No data found
                            </td>
                        </tr>
                    ) : tableData?.tableData?.length > 0 ? (
                        tableData?.tableData?.map((item: any, rowIndex: number) => (
                            <tr key={rowIndex} >
                                {filteredData?.filter((key) => key !== "presence_types")?.map((key, index) => (
                                    <td
                                        key={key}
                                        className={`${(key === "poiCount" && selectedCardType?.poiData) ? "highlight-column poiColumn"
                                            : (key === "ctv" && selectedCardType?.cTVData) ? "ctvColumn" : key === 'eh' && selectedCardType?.EhData ? "ehColumn" : ""}
                                            `.trim()}
                                        style={{
                                            backgroundColor:
                                                key === "poiCount" && selectedCardType?.poiData
                                                    ? "#E5EBFE"
                                                    : key === "ctv" && selectedCardType?.cTVData
                                                        ? "#DEF9DE "
                                                        : key === 'eh' && selectedCardType?.EhData
                                                            ? "#FFE0F5"
                                                            : "#FFFFFF",
                                        }}
                                    >
                                        {key === "geoName" &&
                                            isChecked?.MBP &&
                                            item?.myPresence !== 0 && item?.myPresence !== "N/A" ? (
                                            <div style={{ display: "flex", flexDirection: "row", alignItems: 'center', height: '4.131vh', gap: '6px' }}>
                                                <span
                                                    className={`text-underline secondary-hover curser-pointer`}
                                                    onClick={key === "geoName" ? () => navigateTo(item, RouteConstants.dashboard) : undefined}
                                                >
                                                    {item?.[key]}
                                                </span>
                                                {/* presence icon */}
                                                {isChecked?.MBP && item?.myPresence !== 0 && item?.myPresence !== "N/A" && item?.presence_types ? (
                                                    item.presence_types?.map((presenceData: any, index: number) => (
                                                        <div className='d-flex flex-row align-items-center justify-content-center' style={{ position: 'relative', display: 'flex', borderRadius: '4px', backgroundColor: 'rgb(173 175 177 / 15%)', height: '23px', width: '43px', gap: "4px" }}
                                                            onMouseEnter={() => setHoveredPresence({ row: rowIndex, index })}
                                                            onMouseLeave={() => setHoveredPresence(null)}
                                                        >
                                                            <img
                                                                key={index}
                                                                src={presenceData?.icon}
                                                                alt=""
                                                                className=""
                                                                style={{ height: '1vh' }}
                                                            />
                                                            <Heading
                                                                title={presenceData?.properties?.count}
                                                                type={TypographyType.h6}
                                                                colour={TypographyColor.secondary}
                                                                classname=''
                                                            />
                                                            {hoveredPresence?.row === rowIndex && hoveredPresence?.index === index && (
                                                                <div style={{ position: 'absolute', bottom: '140%', left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
                                                                    <div
                                                                        style={{
                                                                            position: 'relative',
                                                                            backgroundColor: 'white',
                                                                            padding: '10px 14px',
                                                                            borderRadius: '8px',
                                                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                                                            fontSize: '15px',
                                                                            textAlign: 'center',
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        <PresenceToolTip presenceData={presenceData} />
                                                                        <div
                                                                            style={{
                                                                                content: '""',
                                                                                position: 'absolute',
                                                                                top: '100%',
                                                                                left: '50%',
                                                                                transform: 'translateX(-50%)',
                                                                                width: 0,
                                                                                height: 0,
                                                                                borderLeft: '6px solid transparent',
                                                                                borderRight: '6px solid transparent',
                                                                                borderTop: '6px solid white',
                                                                                borderBottomLeftRadius: '5px',
                                                                                borderBottomRightRadius: '5px'
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))
                                                ) : null}
                                            </div>
                                        ) :
                                            key === "rankData" ?
                                                <div className='d-flex flex-row gap-1 ' style={{ alignItems: 'center', height: '4.131vh' }}>
                                                    <Heading
                                                        title={key === 'rankData' ? item[key].rank : item[key]}
                                                        type={key === 'rankData' ? TypographyType.h6 : TypographyType.h5}
                                                        colour={TypographyColor.primary}
                                                        classname={`${key === "rankData" && "rankRecommended"}`}
                                                        style={{ backgroundColor: key === 'rankData' ? item.rankData.color : "none" }}
                                                    />
                                                    <Body
                                                        type={BodyType.p2}
                                                        color={BodyColor.primary}
                                                        classname={'p-0'}
                                                    >
                                                        {key === 'rankData' && (item?.[key]?.normalizedScore === null ? "NA" : item?.[key]?.normalizedScore)}
                                                    </Body>
                                                </div> :
                                                (key === 'poiCount' || selTableHeader?.[key]?.toLowerCase()?.includes('poi')) && item?.[key] == null ?
                                                    <div className="d-flex flex-row" style={{ alignItems: 'center', height: '4.131vh' }}>
                                                        <Heading
                                                            title={"Request PoI"}
                                                            type={TypographyType.h5}
                                                            colour={TypographyColor.primary}
                                                            classname={`p-0 text-underline`}
                                                            onClick={() => {
                                                                setIsOpenDrawer?.(true)
                                                                setIsrequestFromTable?.(true)
                                                                setGeoName?.(item.geoName)
                                                            }}
                                                        />
                                                    </div>
                                                    :
                                                    (
                                                        <div style={{ display: "flex", flexDirection: "row", alignItems: 'center', height: '4.131vh', gap: '3px' }}>
                                                            <span
                                                                className={`${key === 'geoName' && 'text-underline secondary-hover curser-pointer'}`}
                                                                onClick={key === "geoName" ? () => navigateTo(item, RouteConstants.dashboard) : undefined}
                                                            >
                                                                {item?.[key]}
                                                            </span>
                                                        </div>
                                                    )}
                                    </td>
                                ))}
                                {tableheader?.length > 0 && (tableheader?.[1]?.label === 'District' || tableheader?.[1]?.label === 'Sub District' || tableheader?.[1]?.label === 'Pincode' || tableheader?.[1]?.label === 'Village') &&
                                    <td className="position-relative  d-flex  align-items-center" style={{ height: '7.5vh' }} >
                                        <TableMenu
                                            row={item}
                                            cols={
                                                currCompareList?.has(item?.village || item?.sub_district || item?.district || item?.pincode)
                                                    ? COLS.filter(col => col.id !== 'add_compare')
                                                    : COLS.filter(col => col.id !== 'view_comparison')
                                            }
                                            menuRef={menuRef}
                                            onMenuClick={(flag: boolean | undefined) => setOpenedMenu(flag ? null : openedMenu === rowIndex ? null : rowIndex)}
                                            index={rowIndex}
                                            selRow={openedMenu}
                                            showTooltip={new Map([['add_compare', (currCompareList.size >= 9 && openedMenu === rowIndex)]])}
                                            tooltipText='You have reached the limit of 9 regions. Remove some to add more.'
                                            position={isExpanded && rowIndex >= 4 ? 'top' : 'bottom'}
                                        />
                                    </td>}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td
                                colSpan={tableheader?.length}
                                style={{
                                    textAlign: "center",
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                {`${"No"} ${"Data"}`}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* Load More Button */}
            {isExpanded && (searchTerm && (suggestions?.length === 0 || suggestions?.length > 0) ? (suggestions?.length > 6) : tableData?.tableData?.length > 6) && (
                <div
                    style={{
                        height: "auto", // Match button height
                        position: "absolute", // Keep it in the table container
                        bottom: "0px", // Position near the bottom of the container
                        width: "100%",
                        display: "flex",
                        justifyContent: "center", // Center the button horizontally
                        alignItems: "center", // Match button alignment
                        backgroundColor: "white"
                    }}
                >
                    <Button
                        theme={ButtonTheme.primary}
                        size={ButtonSize.large}
                        variant={ButtonVariant.transparent}
                        onClick={handleLoadMore}
                        classname='breakdown-tableBtn text-underline'
                    >
                        Load More
                    </Button>
                </div>
            )}
        </div>
    );
});